import type { DentalXChangeDentalSummaryInfo, SecondaryIdsType } from '@frontend/api-insurance-eligibility-report';
import { i18next } from '@frontend/i18n';
import { TableColumnConfig, Text } from '@frontend/design-system';
import { formatAdditionalInfoObjectToString } from '../../helper';
import { InsuranceReportTableColumnType } from '../../hooks/useInsuranceTableFiltersColumnsStore';
import {
  TableColumnKeyType,
  TableDataType,
  TableFilterKey,
  TablesDataType,
  TuuthFairyTableType,
  Vendors,
} from '../../types/types';
import {
  getAllInsuranceReportTableColumnKeysForDentalXChange,
  getInsuranceReportTableColumnsForDentalXChange,
} from '../../utils/vendors/dentaxchange-helpers';
import {
  getAllInsuranceReportTableColumnKeysForTuuthFairy,
  getInsuranceReportTableColumnsForTuuthFairy,
  prepareLimitationColumn,
} from '../../utils/vendors/tuuthfairy-helpers';

export const getCodeColumnConfig = (width = '50'): TableColumnConfig<TableDataType> => {
  return {
    id: 'code',
    Header: i18next.t('Code', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Code', { ns: 'insuranceEligibilityReport' }),
    accessor: 'code',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getDescriptionColumnConfig = (width?: string): TableColumnConfig<TableDataType> => {
  return {
    id: 'description',
    Header: i18next.t('Description', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Description', { ns: 'insuranceEligibilityReport' }),
    accessor: 'description',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getServiceTypeColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'serviceType',
    Header: i18next.t('Service Type', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Service Type', { ns: 'insuranceEligibilityReport' }),
    accessor: 'serviceType',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getNetworkColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'network',
    Header: i18next.t('Network', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Network', { ns: 'insuranceEligibilityReport' }),
    accessor: 'network',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getCoverageLevelColumnConfig = (width = '120'): TableColumnConfig<TableDataType> => {
  return {
    id: 'coverageLevel',
    Header: i18next.t('Coverage Level', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Coverage Level', { ns: 'insuranceEligibilityReport' }),
    accessor: 'coverageLevel',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getCoverageColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'coverage',
    Header: i18next.t('Coverage', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Coverage', { ns: 'insuranceEligibilityReport' }),
    accessor: 'coverage',
    cellRenderer: (value) => {
      const num = Number(value);
      if (value && !isNaN(num)) {
        return (num * 100).toFixed() + '%';
      }
      return '-';
    },
    ...(width && { width }),
  };
};

export const getAdditionalInformationColumnConfig = (width?: string): TableColumnConfig<TableDataType> => {
  return {
    id: 'additionalInformation',
    Header: i18next.t('Additional Information', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Additional Information', { ns: 'insuranceEligibilityReport' }),
    accessor: 'additionalInformation',
    cellRenderer: (value) => (
      <Text css={{ whiteSpace: 'pre-wrap' }}>
        {typeof value === 'string' ? value : formatAdditionalInfoObjectToString(value)}
      </Text>
    ),
    ...(width && { width }),
  };
};

export const getPlanPeriodColumnConfig = (width = '100'): TableColumnConfig<TableDataType> => {
  return {
    id: 'planPeriod',
    Header: i18next.t('Plan Period', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Plan Period', { ns: 'insuranceEligibilityReport' }),
    accessor: 'planPeriod',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getAmountColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'amount',
    Header: i18next.t('Amount', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Amount', { ns: 'insuranceEligibilityReport' }),
    accessor: 'amount',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getRemainingConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'remaining',
    Header: i18next.t('Remaining', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Remaining', { ns: 'insuranceEligibilityReport' }),
    accessor: 'remaining',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getNetworkNameConfig = (width = '100'): TableColumnConfig<TableDataType> => {
  return {
    id: 'networkName',
    Header: i18next.t('Network Name', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Network Name', { ns: 'insuranceEligibilityReport' }),
    accessor: 'networkName',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getNetworkTypeColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'networkType',
    Header: i18next.t('Network Type', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Network Type', { ns: 'insuranceEligibilityReport' }),
    accessor: 'networkType',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getWaitingPeriodColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'waitingPeriod',
    Header: i18next.t('Waiting Period', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Waiting Period', { ns: 'insuranceEligibilityReport' }),
    accessor: 'waitingPeriod',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getDeductibleAppliesColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'deductibleApplies',
    Header: i18next.t('Deductible Applies', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Deductible Applies', { ns: 'insuranceEligibilityReport' }),
    accessor: 'deductibleApplies',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getNotesColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'notes',
    Header: i18next.t('Notes', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Notes', { ns: 'insuranceEligibilityReport' }),
    accessor: 'notes',
    cellRenderer: (value) => (value.length > 0 && value.join(' ')) || '-',
    ...(width && { width }),
  };
};

export const getCopayColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'coPay',
    Header: i18next.t('Co Pay', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Co Pay', { ns: 'insuranceEligibilityReport' }),
    accessor: 'coPay',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getAgeMaxColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'ageMax',
    Header: i18next.t('Age Max', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Age Max', { ns: 'insuranceEligibilityReport' }),
    accessor: 'ageMax',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getAgeMinColumnConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'ageMin',
    Header: i18next.t('Age Min', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Age Min', { ns: 'insuranceEligibilityReport' }),
    accessor: 'ageMin',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getBenefitLimitationColumnConfig = (width = '100'): TableColumnConfig<TableDataType> => {
  return {
    id: 'limitation',
    Header: i18next.t('Limitation', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Limitation', { ns: 'insuranceEligibilityReport' }),
    accessor: (data: TableDataType) => {
      return prepareLimitationColumn(data as TuuthFairyTableType);
    },
    cellRenderer: (value: string) => value || '-',
    ...(width && { width }),
  };
};

export const getCategoryConfig = (width = '100'): TableColumnConfig<TableDataType> => {
  return {
    id: 'category',
    Header: i18next.t('Category', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Category', { ns: 'insuranceEligibilityReport' }),
    accessor: 'category',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getPeriodUnitConfig = (width = '70'): TableColumnConfig<TableDataType> => {
  return {
    id: 'periodUnit',
    Header: i18next.t('Period Unit', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Period Unit', { ns: 'insuranceEligibilityReport' }),
    accessor: 'periodUnit',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getPlanCoverageDescriptionColumnConfig = (width?: string): TableColumnConfig<TableDataType> => {
  return {
    id: 'planCoverageDescription',
    Header: i18next.t('Description', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Description', { ns: 'insuranceEligibilityReport' }),
    accessor: 'planCoverageDescription',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getMessageColumnConfig = (width?: string): TableColumnConfig<TableDataType> => {
  return {
    id: 'message',
    Header: i18next.t('Message', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Message', { ns: 'insuranceEligibilityReport' }),
    accessor: 'message',
    ...(width && { width }),
    cellRenderer: (value) => {
      return (
        <Text size='medium' css={{ width: '100%' }}>
          {value || '-'}
        </Text>
      );
    },
  };
};

export const getBenefitAgeColumnConfig = (width = '50'): TableColumnConfig<TableDataType> => {
  return {
    id: 'benefitAge',
    Header: i18next.t('Benefit Age', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Benefit Age', { ns: 'insuranceEligibilityReport' }),
    accessor: 'benefitAge',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getPercentColumnConfig = (width = '50'): TableColumnConfig<TableDataType> => {
  return {
    id: 'percent',
    Header: i18next.t('Percent', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Percent', { ns: 'insuranceEligibilityReport' }),
    accessor: 'percent',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getAuthorizationRequiredColumnConfig = (width = '50'): TableColumnConfig<TableDataType> => {
  return {
    id: 'authorizationRequired',
    Header: i18next.t('Authorization Required', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Authorization Required', { ns: 'insuranceEligibilityReport' }),
    accessor: 'authorizationRequired',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getInsuranceTypeColumnConfig = (width = '100'): TableColumnConfig<TableDataType> => {
  return {
    id: 'insuranceType',
    Header: i18next.t('Insurance Type', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Insurance Type', { ns: 'insuranceEligibilityReport' }),
    accessor: 'insuranceType',
    cellRenderer: (value) => value || '-',
    ...(width && { width }),
  };
};

export const getServiceDatesColumnConfig = (width = '120'): TableColumnConfig<TableDataType> => {
  return {
    id: 'serviceDates',
    Header: i18next.t('Service Dates', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Service Dates', { ns: 'insuranceEligibilityReport' }),
    accessor: 'serviceDates',
    cellRenderer: (value: string[]) => value?.join('| ') || '-',
    ...(width && { width }),
  };
};

export const getSecondaryIdsColumnConfig = (width?: string): TableColumnConfig<TableDataType> => {
  return {
    id: 'secondaryIDs',
    Header: i18next.t('Secondary IDs', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Secondary IDs', { ns: 'insuranceEligibilityReport' }),
    accessor: 'secondaryIDs',
    cellRenderer: (value: SecondaryIdsType[]) =>
      value
        ?.map((item) => {
          return `${item.referenceID || ''} ${item.description || ''}`;
        })
        .join(', ') || '-',
    ...(width && { width }),
  };
};

export const getLimitationColumnConfig = (width = '100'): TableColumnConfig<TableDataType> => {
  return {
    id: 'limitation',
    Header: i18next.t('Limitation', { ns: 'insuranceEligibilityReport' }),
    headerLabel: i18next.t('Limitation', { ns: 'insuranceEligibilityReport' }),
    accessor: 'limitation',
    cellRenderer: (value: string) => value || '-',
    ...(width && { width }),
  };
};

export const getInsuranceReportTableColumns = (
  tableName: keyof TablesDataType,
  keys: (keyof DentalXChangeDentalSummaryInfo)[],
  vendor: Vendors | null
) => {
  if (vendor === Vendors.DentalXChange) {
    return getInsuranceReportTableColumnsForDentalXChange(tableName, keys);
  } else if (vendor === Vendors.TuuthFairy) {
    return getInsuranceReportTableColumnsForTuuthFairy(tableName, keys);
  } else {
    throw new Error('Unrecognised Vendor');
  }
};

export const getUniqueValuesByKey = (tableData: TableDataType[] | undefined, key: TableFilterKey | null) => {
  if (!Array.isArray(tableData) || !key) {
    return []; // Return an empty array if the input is not an array.
  }

  return tableData?.reduce((acc: string[], item) => {
    const value = key ? (item[key as keyof TableDataType] as string) : undefined;
    if (value && !acc.includes(value)) {
      acc.push(value);
    }
    return acc;
  }, []);
};

export const sanitizeString = (str: string) => {
  return str.replace(/[-_]/g, ' ');
};

export type InsuranceReportTableColumnKeysType = {
  id: TableColumnKeyType;
  label: string;
};

/**
 * @desc: Get all the column keys for the insurance report table to render it into filter
 */
export const getAllInsuranceReportTableColumnKeys = (
  vendor: Vendors | null = Vendors.DentalXChange
): InsuranceReportTableColumnKeysType[] => {
  if (vendor === Vendors.DentalXChange) {
    return getAllInsuranceReportTableColumnKeysForDentalXChange();
  } else if (vendor === Vendors.TuuthFairy) {
    return getAllInsuranceReportTableColumnKeysForTuuthFairy();
  } else {
    throw new Error('Unrecognised vendor');
  }
};

/**
 * @desc: Get the column configuration for the insurance report table based on the column key.
 */
export const getInsuranceReportTableColumn = (
  columnKey: TableColumnKeyType,
  vendor: Vendors,
  width?: string
): TableColumnConfig<TableDataType> | null => {
  if (!columnKey) return null;

  switch (columnKey) {
    case 'ageMax':
      return getAgeMaxColumnConfig(width);
    case 'ageMin':
      return getAgeMinColumnConfig(width);
    case 'coPay':
      return getCopayColumnConfig(width);
    case 'coverage':
      return getCoverageColumnConfig();
    case 'deductibleApplies':
      return getDeductibleAppliesColumnConfig(width);
    case 'waitingPeriod':
      return getWaitingPeriodColumnConfig(width);
    case 'limit':
      return getLimitationColumnConfig(width);
    case 'networkType':
      return getNetworkTypeColumnConfig(width);
    case 'description':
      return getDescriptionColumnConfig(width);
    case 'notes':
      return getNotesColumnConfig(width);
    case 'code':
      return getCodeColumnConfig(width);
    case 'serviceType':
      return getServiceTypeColumnConfig(width);
    case 'network':
      return getNetworkColumnConfig(width);
    case 'coverageLevel':
      return getCoverageLevelColumnConfig(width);
    case 'additionalInformation':
      return getAdditionalInformationColumnConfig(width);
    case 'planPeriod':
      return getPlanPeriodColumnConfig(width);
    case 'amount':
      return getAmountColumnConfig(width);
    case 'planCoverageDescription':
      return getPlanCoverageDescriptionColumnConfig(width);
    case 'message':
      return getMessageColumnConfig(width);
    case 'benefitAge':
      return getBenefitAgeColumnConfig(width);
    case 'percent':
      return getPercentColumnConfig(width);
    case 'authorizationRequired':
      return getAuthorizationRequiredColumnConfig(width);
    case 'insuranceType':
      return getInsuranceTypeColumnConfig(width);
    case 'serviceDates':
      return getServiceDatesColumnConfig(width);
    case 'secondaryIDs':
      return getSecondaryIdsColumnConfig(width);
    case 'limitation':
      return vendor === Vendors.TuuthFairy ? getBenefitLimitationColumnConfig(width) : getLimitationColumnConfig(width);
    case 'remaining':
      return getRemainingConfig(width);
    case 'networkName':
      return getNetworkNameConfig(width);
    case 'category':
      return getCategoryConfig(width);
    case 'periodUnit':
      return getPeriodUnitConfig(width);
    default:
      return null; // Return null if no matching column key is found
  }
};

export const getInsuranceReportTableColumnsListByIds = (
  tableColumnsListByTableId: InsuranceReportTableColumnType[],
  vendor: Vendors
) => {
  const tableColumnsList: TableColumnConfig<TableDataType>[] = [];
  if (!tableColumnsListByTableId?.length) {
    return [];
  }
  tableColumnsListByTableId.forEach((column) => {
    if (!column?.isChecked) {
      return;
    }
    const columnConfig = getInsuranceReportTableColumn(column.id, vendor);
    if (columnConfig) {
      tableColumnsList.push(columnConfig);
    }
  });
  return tableColumnsList;
};
